import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormCard, DatePicker, Checkbox, TimeInput, Select } from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { getRequiredRule } from 'screens/Rostering/utils'
import { getHasReoccurence } from '../utils'
import dayjs from 'dayjs'
import AsterixComponent from '../components/Asterix'
import { InputNumber, Form, FormInstance } from 'antd'
import { Tag } from 'antd'
import { Frequency, ShortDay } from '@lmc/ts-common'

type PatternFormCardProps = {
  form: FormInstance
  formData: any
  setFormData: (data: any) => void
  onStartTimeChanged: (value?: string) => void
  onEndTimeChanged: (value?: string) => void
  onFrequencyValue: (value?: string) => void
  onIntervalValue: (value?: number) => void
  onDaysValue: (value?: string[]) => void
}

const PatternFormCard: FC<PatternFormCardProps> = ({
  form,
  formData,
  setFormData,
  onStartTimeChanged,
  onEndTimeChanged,
  onFrequencyValue,
  onIntervalValue,
  onDaysValue,
}) => {
  const { t } = useTranslation()
  const optimisticWeekStartDate = formData?.patternStart
    ? dayjs(formData?.patternStart).day()
    : undefined

  const hasReoccurence = getHasReoccurence(formData)

  const [selectedFrequency, setSelectedFrequency] = useState<
    string | undefined
  >(formData?.frequency || Frequency.DAILY)

  const [selectedInterval, setSelectedInterval] = useState<number | undefined>(
    formData?.interval || 1,
  )

  const daysOptionsValues = daysOptions?.map((item) => item?.value)

  const [selectedDays, setSelectedDays] = useState<string[] | undefined>(
    formData?.specificDays || daysOptionsValues,
  )

  const [noEndDate, setNoEndDate] = useState(false)

  const handleChangeFrequencyType = (value: string) => {
    setSelectedFrequency(value as Frequency)
    if (selectedDays?.length !== 7 && value === Frequency.DAILY) {
      setSelectedDays(daysOptionsValues)
    }
    if (value === Frequency.WEEKLY && !!optimisticWeekStartDate) {
      setSelectedDays([daysOptionsValues[optimisticWeekStartDate - 1]])
    }
  }

  const handleChangeInterval = (value?: unknown) => {
    setSelectedInterval((value as number) || 1)
  }

  const handleChangeDay = (tag: string, checked: boolean) => {
    const nextSelectedDays = checked
      ? [...(selectedDays || []), tag]
      : (selectedDays || []).filter((t) => t !== tag)
    setSelectedDays(nextSelectedDays)
    if (nextSelectedDays?.length !== 7) {
      setSelectedFrequency(Frequency.WEEKLY)
    } else {
      setSelectedFrequency(Frequency.DAILY)
    }
  }

  const handleNoEndDateChange = (e: any) => {
    const isChecked = e.target.checked
    setNoEndDate(isChecked)

    if (isChecked) {
      setFormData({
        ...formData,
        hasPatternEndDate: false,
        patternEnd: null,
      })
    } else {
      setFormData({
        ...formData,
        hasPatternEndDate: true,
      })
    }
  }

  const handlePatternEndChange = (value: any) => {
    let hasEndDate = false

    if (value && !noEndDate) {
      hasEndDate = true
    } else if (!value && !noEndDate) {
      hasEndDate = false
    } else {
      hasEndDate = formData.hasPatternEndDate
    }

    form.setFieldValue('patternEnd', value)

    setFormData({
      ...formData,
      patternEnd: value,
      hasPatternEndDate: hasEndDate,
    })
  }

  useEffect(() => {
    onFrequencyValue?.(selectedFrequency)
  }, [selectedFrequency])

  useEffect(() => {
    onDaysValue?.(selectedDays)
  }, [selectedDays])

  const hasOneInterval = selectedInterval === 1

  useEffect(() => {
    onIntervalValue?.(selectedInterval)
  }, [selectedInterval])

  const showDaysPicker =
    (selectedFrequency === Frequency.DAILY && hasOneInterval) ||
    selectedFrequency === Frequency.WEEKLY

  const getEveryLabel = (frequency?: string) => {
    if (frequency === Frequency.MONTHLY) {
      return 'Month'
    }

    if (frequency === Frequency.WEEKLY) {
      return 'Week'
    }

    return 'Day'
  }

  const frequencyOptions = [
    { label: 'Daily', value: Frequency.DAILY },
    { label: 'Weekly', value: Frequency.WEEKLY },
    { label: 'Monthly', value: Frequency.MONTHLY },
  ]

  return (
    <FormCard level={2} padding="16px">
      <StartRow>
        <StartColumn>
          <Form.Item
            name="patternStart"
            label={<StyledText>{t('STARTS')}</StyledText>}
            rules={[getRequiredRule(t('STARTS'))]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <DatePicker maxDate={formData?.patternEnd} />
          </Form.Item>
          <Form.Item name="reoccurenceTooglePattern" valuePropName="checked">
            <Checkbox
              onChange={(e) => {
                form.setFieldValue('reoccurenceTooglePattern', e.target.checked)
                setFormData({
                  ...formData,
                  reoccurenceTooglePattern: e.target.checked,
                })
              }}
            >
              <StyledText>{t('MAKE_RECURRING')}</StyledText>
            </Checkbox>
          </Form.Item>
        </StartColumn>
        <TimeWindowColumn>
          <Form.Item
            label={
              <StyledText>
                <AsterixComponent />
                {t('TIME_WINDOW')}
              </StyledText>
            }
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <TimeInput
              startTime={formData?.startTime}
              onEndTimeChanged={onEndTimeChanged}
              onStartTimeChanged={onStartTimeChanged}
            />
          </Form.Item>
        </TimeWindowColumn>
      </StartRow>

      {hasReoccurence && (
        <>
          <RecurringRow>
            <EndsColumn>
              <Form.Item
                name="patternEnd"
                label={<StyledText>{t('ENDS')}</StyledText>}
                rules={!noEndDate ? [getRequiredRule(t('ENDS'))] : []}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <DatePicker
                  minDate={formData?.patternStart}
                  disabled={noEndDate}
                  onChange={handlePatternEndChange}
                />
              </Form.Item>
              <Form.Item
                name="hasPatternEndDate"
                hidden
                initialValue={formData?.hasPatternEndDate ?? false}
              ></Form.Item>
              <Checkbox onChange={handleNoEndDateChange} checked={noEndDate}>
                <StyledText>{t('NEVER')}</StyledText>
              </Checkbox>
            </EndsColumn>
            <FrequencyColumn>
              <Form.Item
                label={
                  <StyledText>
                    <AsterixComponent />
                    {t('FREQUENCY')}
                  </StyledText>
                }
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Form.Item>
                  <Select
                    options={frequencyOptions}
                    onChange={(value) => handleChangeFrequencyType(value)}
                    value={selectedFrequency}
                  />
                </Form.Item>
                {[
                  Frequency.DAILY,
                  Frequency.WEEKLY,
                  Frequency.MONTHLY,
                ]?.includes(selectedFrequency as Frequency) && (
                  <EveryDaysWrapper>
                    <StyledText>Every</StyledText>
                    <StyledInput
                      onChange={handleChangeInterval}
                      value={selectedInterval}
                    />
                    <StyledText>
                      {getEveryLabel(selectedFrequency)}(s)
                    </StyledText>
                  </EveryDaysWrapper>
                )}
                {showDaysPicker && (
                  <CheckableOuterWrapper>
                    <StyledText>
                      <AsterixComponent />
                      Specific days:
                    </StyledText>
                    <CheckableWrapper>
                      {daysOptions.map((tag) => (
                        <CheckableTag
                          key={tag.value}
                          checked={!!selectedDays?.includes(tag.value)}
                          onChange={(checked) =>
                            handleChangeDay(tag.value, checked)
                          }
                          style={{
                            color: selectedDays?.includes(tag.value)
                              ? 'white'
                              : 'black',
                          }}
                        >
                          {tag.label}
                        </CheckableTag>
                      ))}
                    </CheckableWrapper>
                  </CheckableOuterWrapper>
                )}
              </Form.Item>
            </FrequencyColumn>
          </RecurringRow>
        </>
      )}
    </FormCard>
  )
}

const StartRow = styled.div`
  display: flex;
  gap: 24px;
`

const StartColumn = styled.div`
  width: 50%;

  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const TimeWindowColumn = styled.div`
  width: 50%;

  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const RecurringRow = styled.div`
  display: flex;
  gap: 24px;
`

const EndsColumn = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const FrequencyColumn = styled.div`
  width: 50%;

  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
`

const CheckableTag = styled(Tag.CheckableTag)`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  align-items: center;
  display: flex;
  justify-content: center;
  font-size: 16px;
`

const StyledInput = styled(InputNumber)`
  margin-left: 8px;
  margin-right: 8px;
`

const StyledText = styled(Text)`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const CheckableWrapper = styled.div`
  display: flex;
  margin-top: 8px;
`

const CheckableOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const EveryDaysWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
`

export const daysOptions = [
  { value: ShortDay.Mon, label: 'M' },
  { value: ShortDay.Tue, label: 'Tu' },
  { value: ShortDay.Wed, label: 'W' },
  { value: ShortDay.Thu, label: 'Th' },
  { value: ShortDay.Fri, label: 'F' },
  { value: ShortDay.Sat, label: 'Sa' },
  { value: ShortDay.Sun, label: 'Su' },
]

export default PatternFormCard
